import React, { useState, useRef } from 'react';
import { SearchApp } from '../../search/components/search-app';
import { SearchList } from '../../search/components/search-list';
import { All, Filter} from '../../search/search-model';
import { BtnCheckBoxFilter } from '../../search/components/btn-check-box-filter';
import { PageModel, ArticlePage, ViewType } from './models';
import { ArticleCardView } from './article-card-view';
import { ArticleListView } from './article-list-view';
import { ListViewSorts } from './list-view-sort';
import { TcsmModal } from '../../global/components/tcsm-modal';

export interface Props {
  pageModel: PageModel;
}

const allOptions = {
  filterKey: 'category',
  filterValue: All,
  filterDisplayValue: All,
};

function TopicPageSearch(props: Props) {
  const { pageModel } = props;

  const viewMode = pageModel.viewMode;
  const canToggle = viewMode === 'CardViewDefault' || viewMode === 'ListViewDefault';
  const defaultToList = viewMode === 'ListViewDefault' || viewMode === 'ListViewOnly';

  const [view, setView] = useState(defaultToList ? ViewType.list : ViewType.card);
  const [videoLink, setVideoLink] = useState<string | null>(null);

  const categories = pageModel?.articlePages
    ?.map(article => {
      const original = article?.category || '';
      const decoded = decodeURIComponent(original.replace(/\+/g, ' '));
      return decoded;
    })
    .filter(Boolean);

  const uniqueArray = Array.from(new Set(categories));
  const filterOptions = [allOptions].concat(
    uniqueArray.map(item => {
      const normalized = item.toLowerCase();
      return {
        filterKey: 'category',
        filterValue: normalized,
        filterDisplayValue: item,
      };
    })
  );

  const queryParams = new URLSearchParams(window?.location?.search);
  const rawFilter = queryParams.get('filter') || '';

  const rawParam = decodeURIComponent(rawFilter.replace(/\+/g, ' '));

  const filterValues = rawParam
    .split(',')
    .map(v => v.trim().toLowerCase())
    .filter(v => v);

  const initialFilters = React.useMemo<Filter[]>(() => {
    if (!filterValues.length) return [];
    return [
      {
        filterKey: 'category',
        filterOptions: filterValues.map(option => ({
          filterKey: 'category',
          filterValue: option,
          filterDisplayValue: option,
        })),
        partialMatch: false,
      }
    ];
  }, [rawParam]);

  const initialFilterRef = useRef<Filter[]>(initialFilters);

  return (
    <div>
      <SearchApp
        initialList={pageModel?.articlePages}
        appName='topic-page-search'
        initialFilters={initialFilterRef.current}
      >
        <BtnCheckBoxFilter
          name='category'
          options={filterOptions}
          label={pageModel?.filterBy}
          clearText={pageModel.clearFilterText}
          setView={setView}
          view={view}
          hideToggle={!canToggle}
          initialValue={rawParam}
        />
        
        {view === ViewType.list && <ListViewSorts pageModel={pageModel} />}
        {view === ViewType.card ? (
          <div className='topic-grid'>
            <SearchList
              showAll
              renderList={(item: ArticlePage) => (
                <ArticleCardView
                  article={item}
                  setVideo={setVideoLink}
                  showDatesAndCategory={
                    pageModel.hideDatesAndCategories !== 'True'
                  }
                />
              )}
            />
          </div>
        ) : (
          <div className='list-container'>
            <SearchList
              showAll
              renderList={(item: ArticlePage) => (
                <ArticleListView
                  article={item}
                  setVideo={setVideoLink}
                  showDatesAndCategory={
                    pageModel.hideDatesAndCategories !== 'True'
                  }
                />
              )}
            />
          </div>
        )}
      </SearchApp>
      <TcsmModal
        open={!!videoLink}
        handleClose={() => setVideoLink(null)}
      >
        <iframe
          width='640'
          height='360'
          src={videoLink || ''}
          frameBorder='0'
          allowFullScreen
        ></iframe>
      </TcsmModal>
    </div>
  );
}

export default TopicPageSearch;
